import { type PropsWithChildren } from 'react';
import { type MarketingPreferencesResponse } from '@cx-sdk/myoc';
import { type NavV2Response } from '@cx-web/api-clients/nav-api/types';
import { type SiteConfig } from '@cx-web/api-clients/site-config-api/types';
import { type FooterV2Response, type SerializeFrom } from '@cx-web/domain-types';
import { type MyOCData } from '@cx-web/domain-types/myoc';
import Betslip from '@cx-web/feature-betslip/components/betslip';
import { MyOCAuthProvider } from '@cx-web/feature-myoc';
import { NavDataProvider } from '@cx-web/ui-shell/nav-data-context';
import { UIConfigProvider } from '@cx-web/ui-shell/ui-config-context';

import CookieBanner from '~/components/composites/CookieBanner';
import { LegacyAuthModal } from '~/components/composites/legacy-auth-modal';
import { type loader } from '~/routes/$/loader.server';

import { LayoutComponents } from './components/LayoutComponents';

export type StandardLayoutLoaderData = {
  footerData?: FooterV2Response | null;
  navData: NavV2Response;
  siteConfig: SiteConfig;
};

export function StandardLayout({
  adUnitConfiguration,
  baseUrlWithProtocolAndRepub,
  children,
  deviceType,
  footerData,
  isAdmiralCookieBannerEnabled,
  marketingPreferences,
  myocConfig,
  myocData,
  navData,
  oddsFormat,
  repub,
  resourcesUrl,
  showCookieBanner,
}: PropsWithChildren<
  SerializeFrom<typeof loader> &
    StandardLayoutLoaderData & {
      marketingPreferences?: MarketingPreferencesResponse;
      showCookieBanner?: boolean;
    }
>) {
  const renderCookieBanner = () => {
    const permittedRepubs = ['ES', 'IT'];
    if (!permittedRepubs.includes(repub.toUpperCase())) {
      return null;
    }
    return <CookieBanner repub={repub as 'ES' | 'IT'} showBanner={showCookieBanner} />;
  };

  return (
    <UIConfigProvider
      deviceType={deviceType}
      isAdmiralCookieBannerEnabled={isAdmiralCookieBannerEnabled}
      resourcesUrl={resourcesUrl}
    >
      <MyOCAuthProvider
        marketingPreferences={marketingPreferences}
        myocConfig={myocConfig}
        myocData={myocData as MyOCData}
      >
        <NavDataProvider navData={navData}>
          <LayoutComponents
            adUnitConfiguration={adUnitConfiguration}
            footerData={footerData ?? undefined}
            userOddsFormat={oddsFormat}
          >
            {children}
          </LayoutComponents>
        </NavDataProvider>
        <Betslip />
        <LegacyAuthModal baseUrl={baseUrlWithProtocolAndRepub} />
        {renderCookieBanner()}
      </MyOCAuthProvider>
    </UIConfigProvider>
  );
}
